<template>
  <MDBContainer v-if="show">
    <Menu />
    <template v-if="accessData._restricted_access">
      <div class="d-flex justify-content-between align-items-center p-3 mb-2 bg-info bg-gradient text-dark"
        v-if="!accessData._client_type_approved_at">
        {{ $t('onboarding.waitForAdminApproval') }}
      </div>

      <Slider :slides="slides" />
    </template>
    <template v-else>
      <MDBRow>
        <MDBCol col="lg-3" :class="`column-order justify-content-start ${props.switchOrderMobile ? 'order-lg-1 order-2 mt-lg-0 mt-5' : null
          }`">
          <LeftColumnLV />
        </MDBCol>
        <MDBCol col="lg-9" :class="`${props.switchOrderMobile ? 'order-lg-2 order-1' : null}`">
          <props.content />
        </MDBCol>
      </MDBRow>
      <MDBRow class="mt-2" :class="{ 'align-items-stretch': isDashoard }">
        <MDBCol col="lg-3" :class="{ 'd-flex flex-column': isDashoard }">
          <div v-if="isDashoard" class="dashboard-teaser-heading">
            <h5>{{ seeTextOverPointBox() && context.canSeePointsBox ? $t('points.dashboard.dashboardTitle') : ''
              }}&nbsp;</h5>
          </div>
          <PointBox :stretch="isDashoard" />
        </MDBCol>
        <MDBCol col="lg-9" :class="{ 'd-flex': isDashoard }">
          <props.data />
        </MDBCol>
      </MDBRow>
      <MDBRow class="mt-3 align-items-stretch">
        <MDBCol col="lg-3" class="d-flex">
          <KickbackBox />
        </MDBCol>
        <MDBCol col="lg-9" class="d-flex">
          <div class="mt-3 w-100 nl-banner--min-height"
            v-if="route.name == 'dashboard' && showTenantBanner && showExtraBanner">
            <a href="https://aanbieding.bridgestone.nl/upload-factuur-wasbeurt/" target="_blank"
              class="w-100 h-100 nl-banner"></a>
          </div>
        </MDBCol>
      </MDBRow>
      <AppsPanel v-if="isDashoard && context.canSeeApps" />
    </template>
    <Footer />
  </MDBContainer>

  <div v-if="showFirstInitialStep" class="initial-step-overlay">
    <div class="initial-step-dialog">
      <button v-if="canCloseInitialStepModal" type="button" class="btn-close modal-close-btn" aria-label="Close"
        @click="closeFirstInitialStepModal"></button>
      <PartOne />
    </div>
  </div>
  <MDBModal v-model="showSecondInitialStep" tabindex="-1" centered size="xl" class="bg-modal">
    <PartTwo />
  </MDBModal>
  <MDBModal v-model="showThirdInitialStep" tabindex="-1" centered size="xl" class="bg-modal">
    <PartThree />
  </MDBModal>
</template>

<script setup>
import { MDBCol, MDBRow, MDBContainer, MDBModal } from "mdb-vue-ui-kit";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import Menu from "@/components/Layout/Menu.vue";
import Footer from "@/components/Layout/Footer.vue";
import LeftColumnLV from "@/components/Layout/LeftColumnLV.vue";
import AppsPanel from "@/components/AppsPanel/AppsPanel.vue";
import PartOne from "@/components/Intro/InitialSurvey/PartOne.vue";
import PartTwo from "@/components/Intro/InitialSurvey/PartTwo.vue";
import PartThree from "@/components/Intro/InitialSurvey/PartThree.vue";
import PointBox from "@/components/Dashboard/PointBox.vue";
import KickbackBox from "@/components/Dashboard/KickbackBox.vue";
import Slider from "@/components/Dashboard/Slider.vue";
import { defineProps, ref, computed, onMounted, watch } from "vue";
import { useTenantConfig } from "@/composables/useTenantConfig";
import { useAllowOnboard, userCanSeeTextOverPointBox } from "@/composables/useProfileFieldCheck";
import { useItemsConditions } from '@/composables/useItemsConditions'
import { ROUTE_NAMES } from "@/configs/constants";
import TokenService from "@/services/storage.service";

const store = useStore();
const route = useRoute();
const { tenantConfig } = useTenantConfig();
const { allowOnboard } = useAllowOnboard(tenantConfig);
const { seeTextOverPointBox } = userCanSeeTextOverPointBox(tenantConfig);

const { getContext } = useItemsConditions()
const context = computed(() => getContext())

const props = defineProps({
  content: Object,
  switchOrderMobile: Boolean,
  data: Object,
});

const me = computed(() => store.getters.getMe);
const meIsPending = computed(() => store.getters.getIsMePending);
const initialStep = computed(() => {
  const stepFromStore = Number(store.getters.getInitialSurveyStep);
  if (Number.isFinite(stepFromStore) && stepFromStore > 0) {
    return stepFromStore;
  }

  const stepFromMe = Number(me.value?.light_version_intro ?? me.value?.intro);
  if (Number.isFinite(stepFromMe) && stepFromMe > 0) {
    return stepFromMe;
  }

  return 0;
});
const isFirstInitialStep = computed(() => initialStep.value === 1);
const isSecondInitialStep = computed(() => initialStep.value === 2);
const isThirdInitialStep = computed(() => initialStep.value === 3);
const accessData = computed(() => store.getters.getAccessData ?? {});
const slides = computed(() => store.getters.getUserSlides);
const permissions = computed(() => store.getters.getPermissions);

const showExtraBanner = computed(() => {
  return permissions.value && TokenService.showElement("kickBackFeeBox.see");
});
const showTenantBanner = computed(
  () => tenantConfig.value?.showNlCarwashBanner ?? false
);
const isDashoard = computed(() => route.name === ROUTE_NAMES.DASHBOARD);
const canCloseInitialStepModal = computed(() => Boolean(sessionStorage.getItem("admin_hashid")));
const hideFirstInitialStep = ref(false);
const firstInitialModalWasOpen = ref(false);

const show = ref(false); // Used to control UI state

const shouldShowFirstInitialStep = computed(
  () => !hideFirstInitialStep.value && isFirstInitialStep.value && allowOnboard()
);
const showFirstInitialStep = ref(false);
const showSecondInitialStep = computed(() => isSecondInitialStep.value && allowOnboard());
const showThirdInitialStep = computed(() => isThirdInitialStep.value && allowOnboard());

const closeFirstInitialStepModal = () => {
  if (canCloseInitialStepModal.value) {
    hideFirstInitialStep.value = true;
    showFirstInitialStep.value = false;
  }
};

watch(
  () => me.value?.id,
  () => {
    hideFirstInitialStep.value = false;
  }
);

watch(initialStep, (step) => {
  if (step === 1) {
    hideFirstInitialStep.value = false;
  }
});

watch(showFirstInitialStep, (isOpen) => {
  firstInitialModalWasOpen.value = isOpen;
});

watch(
  shouldShowFirstInitialStep,
  (shouldOpen) => {
    showFirstInitialStep.value = shouldOpen;
  },
  { immediate: true }
);

watch(showFirstInitialStep, (isOpen, wasOpen) => {
  if (
    wasOpen &&
    !isOpen &&
    canCloseInitialStepModal.value &&
    shouldShowFirstInitialStep.value
  ) {
    hideFirstInitialStep.value = true;
  }
});

if (process.env.NODE_ENV !== "production") {
  watch(
    [
      () => store.getters.tenantId,
      () => allowOnboard(),
      initialStep,
      () => me.value?.intro,
      () => me.value?.light_version_intro,
      showFirstInitialStep,
      () => hideFirstInitialStep.value,
      () => accessData.value?._restricted_access,
      shouldShowFirstInitialStep,
    ],
    ([tenantId, onboardEnabled, step, intro, lightIntro, modalOpen, hidden, restricted, shouldOpen]) => {
      // eslint-disable-next-line no-console
      console.debug("[OnboardingModalDebug]", {
        tenantId,
        onboardEnabled,
        step,
        intro,
        lightIntro,
        shouldOpen,
        modalOpen,
        hidden,
        restricted,
      });
    },
    { immediate: true }
  );
}

onMounted(async () => {
  if (!meIsPending.value) {
    await store.dispatch("me").finally(() => {
      show.value = true;
    });
  } else {
    const unwatch = watch(meIsPending, (pending) => {
      if (!pending) {
        show.value = true;
        unwatch();
      }
    });
  }
});
</script>


<style lang="scss" scoped>
@import "@/styles/_variables.scss";
@import "@/styles/bottom_banner.scss";
@import "@/styles/dashboard_teaser.scss";

.bg-modal {
  background-color: rgba(0, 0, 0, 0.8) !important;
}

.extra-modal-level {
  z-index: 1056 !important;
}

.modal-close-btn {
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 2;
}

.initial-step-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.initial-step-dialog {
  position: relative;
  width: min(1140px, 100%);
  max-height: calc(100vh - 32px);
  overflow: auto;
}
</style>
